import { useAddIdPrefix, useComprehensiveTopNav, useCustomToast, useSocket, useWindowResize } from 'hooks';
import routes from 'lib/api/routes';
import { Image, LoadingIndicator, Typography } from 'lib/components';
import Tag from 'lib/components/Tag';
import { getLocation, JobStatus, JobStatusType, MessageStatusType, Timing, toHHMMSS } from 'lib/util';
import { nanoid as ID } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectConversations,
	useSelectCurrentUser,
	useSelectJob,
	useSelectRequest,
	useSelectSettings,
} from 'store';
import { Conversation, Job } from 'types';
import { uuid } from 'uuidv4';
import adjustTime from '../../lib/adjustTime';
import MapView from '../../lib/components/MapView';
import formatJobAttributeValue from '../../lib/util/formatJobAttributeValue';
import getJobAttributeIgnores from '../../lib/util/getJobAttributeIgnores';
import getJobAttributeName from '../../lib/util/getJobAttributeName';
import ChatPopup from './ChatPopup';
import FileList from './FileList';
import classes from './index.module.scss';

function physicianPhone(job: Job): string | null {
	const finished = [
		JobStatusType.COMPLETED,
		JobStatusType.ABORTED,
		JobStatusType.CANCELLED,
		JobStatusType.VOIDED,
	].includes(job.status);
	if (finished) {
		return job.orderingPhysicianPhone || null;
	}
	return job.currentPhysicianPhone || job.orderingPhysicianPhone || null;
}
import JobCustomCostList from './JobCustomCostList';
import JobStatusSelect from './JobStatusSelect';
import Notes from './Notes';
import Timeline from './Timeline';

/**
 * Props for the JobScreenView component
 */
interface Props {
	job: Job; // Job data to display
	conversation: Conversation; // Associated conversation for chat functionality
}

/**
 * Job detail screen component
 * Fetches and displays detailed information about a specific job
 * Includes job status, timeline, chat functionality, and more
 */
const JobScreen = () => {
	// Get job ID from URL parameters
	const { id } = useParams<{ id: string }>();
	const jobId = parseInt(id);

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Helper function to get job by ID
	const getJobById = useSelectJob();
	// Conversations data from Redux store
	const conversations = useSelectConversations();

	// Job data state
	const [job, setJob] = useState<Job>();
	// Associated conversation for chat functionality
	const [conversation, setConversation] = useState<Conversation>();
	// Counter to track when both job and conversation data have been loaded
	const [fetchedCount, setFetchedCount] = useState(0);

	/**
	 * Fetch job and conversations data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchJob(parseInt(id)));
		dispatch(actions.fetchConversations());
	}, []);

	/**
	 * Monitor API requests to track when job and conversation data have finished loading
	 * Updates local state with fetched data when available
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		// Handle conversations fetch request
		if (RM.req(routes.CONVERSATION, 'GET')) {
			if (RM.succeeded()) {
				// Find the conversation associated with this job
				const findConversation = conversations.list.find(
					(conversation) => String(conversation.jobId) === String(jobId)
				);

				if (findConversation) {
					setConversation(findConversation);
				}
				setFetchedCount((prev) => prev + 1);
			}
		}

		// Handle job fetch request
		if (RM.req(routes.JOB_ID(jobId), 'GET')) {
			if (RM.succeeded()) {
				const job = getJobById(jobId);

				if (job) {
					setJob(job);
				}
				setFetchedCount((prev) => prev + 1);
			}
		}
	}, [active]);

	/**
	 * Keep local conversation in sync with Redux so ChatPopup refreshes live.
	 */
	useEffect(() => {
		const findConversation = conversations.list.find(
			(conversation) => String(conversation.jobId) === String(jobId)
		);

		if (findConversation) {
			setConversation(findConversation);
		}
	}, [conversations.updatedAt, conversations.list, jobId]);

	// Show loading indicator until both job and conversation data are loaded
	if (fetchedCount < 2) return <LoadingIndicator />;

	// Show loading indicator if either job or conversation data is missing
	if (!job || !conversation) return <LoadingIndicator />;

	// Render the job screen view with the loaded data
	return <JobScreenView job={job} conversation={conversation} />;
};

/**
 * Main job screen view component
 * Displays detailed job information and provides interaction capabilities
 *
 * @param job - The job data to display
 * @param conversation - The associated conversation for chat functionality
 */
const JobScreenView = ({ job, conversation }: Props) => {
	// Reference to the container element for positioning
	const containerRef = useRef<HTMLDivElement>(null);

	// Settings data from Redux store (for time zone adjustments)
	const settingsState = useSelectSettings();

	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Helper function to add prefix to job IDs
	const addPrefix = useAddIdPrefix();
	// Navigation helper
	const { push } = useHistory();
	// Get URL query parameters
	const { search } = useLocation();
	// Check if chat should be open from URL parameter
	const chat = new URLSearchParams(search).get('chat');
	// WebSocket connection for real-time communication
	const { socket } = useSocket();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Current logged-in user information
	const me = useSelectCurrentUser();
	// Permission check for job update capability
	const { canUpdateJob } = useSelectAuthorization();
	// Active API request tracking
	const { active } = useSelectRequest();

	// Controls visibility of the chat popup
	const [chatPopupOpen, setChatPopupOpen] = useState(chat === 'open' ? true : false);
	// Current message being composed in the chat
	const [message, setMessage] = useState('');
	// Current job status (initialized from job data)
	const [status, setStatus] = useState<number | undefined>(job.status);
	// Responsive design helpers
	const { isSmallDevice, width, isSmallerDevice } = useWindowResize();

	// Determines if the send message button should be enabled
	// Only enabled if there's a non-empty message
	const canProceed = useMemo<boolean>(() => {
		return message !== '';
	}, [message]);

	// const handleCallButtonClick = useCallback(() => {
	// 	const link = document.createElement('a');
	// 	link.href = `tel:${job.nurse!.user.phoneNumber}`;
	// 	link.click();
	// }, [job.id]);

	// const handleEmailButtonClick = useCallback(() => {
	// 	const link = document.createElement('a');
	// 	link.href = `mailto:${job.nurse!.user.email}`;
	// 	link.click();
	// }, [job.id]);

	/**
	 * Handle sending a new message in the chat
	 * Emits a socket event and updates Redux state
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const handleSend = useCallback(() => {
		// Don't send if message is empty or socket is not connected
		if (!canProceed || !socket) {
			return;
		}

		// Generate a temporary ID for the message
		const temporaryId = uuid();
		// Emit socket event to create a new message
		socket.emit('conversation:message:create', conversation.id, temporaryId, message);
		// Update Redux state with the new message
		dispatch(
			actions.messageCreated({
				conversationId: conversation.id,
				temporaryId,
				userId: me.id,
				message,
			})
		);

		// Clear the message input
		setMessage('');
	}, [dispatch, canProceed, socket, message]);

	/**
	 * Handle marking all messages as read
	 * Emits a socket event and updates Redux state
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const handleRead = useCallback(() => {
		// Don't proceed if socket is not connected
		if (!socket) {
			return;
		}

		// Emit socket event to mark messages as read
		socket.emit('conversation:messages:read', conversation.id);
		// Update Redux state to mark messages as read
		dispatch(
			actions.messagesRead({
				conversationId: conversation.id,
				userId: me.id,
			})
		);

		// Clear the message input
		setMessage('');
	}, [dispatch, socket]);

	/**
	 * Set up socket listener for nurse location updates
	 * Currently just logs the updates to the console
	 */
	useEffect(() => {
		if (!socket) return;

		socket.on(
			'nurse:location:updates',
			(nurseId: number, jobId: number, jobStatus: JobStatusType, longitude: number, latitude: number) => {
				console.log({ nurseId, jobId, jobStatus, longitude, latitude });
			}
		);
	}, [socket]);

	/**
	 * Monitor API requests for job status updates
	 * Shows toast notifications for success/failure
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.JOB_STATUS(job.id), 'POST')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
			}
		}
	}, [active]);

	/**
	 * Mark messages as read when chat popup is opened
	 */
	useEffect(() => {
		if (!chatPopupOpen) {
			return;
		}

		handleRead();
	}, [chatPopupOpen, handleRead]);

	/**
	 * Handle job status change
	 * Dispatches action to update job status on the server
	 *
	 * @param status - The new status value to set
	 */
	const handleStatusChange = (status: number) => {
		dispatch(actions.updateJobStatus(job.id, status));
	};

	/**
	 * Generate a list of job attributes to display in the UI
	 * Groups attributes by wait entries and job status
	 * Memoized to prevent unnecessary recalculations
	 */
	const attributesDetails = React.useMemo(() => {
		let list = [];

		// Process wait time entries and their attributes
		for (const waitEntry of job.waits) {
			list.push({
				title: `Wait Time: Actual (${waitEntry.actualTime}m) Rounded (${waitEntry.roundedTime}m)`,
				element: (
					<>
						{waitEntry.attributes
							// Filter out attributes that should be ignored
							.filter((element) => getJobAttributeIgnores().includes(element.name) === false)
							.map((element) => (
								<Typography
									key={`${waitEntry.id}-${element.id}`}
									variant={isSmallerDevice ? 'h5' : 'h6'}
								>
									{getJobAttributeName(element.name)}: <b>{formatJobAttributeValue(element.value)}</b>
								</Typography>
							))}
					</>
				),
			});
		}

		// Process job attributes grouped by job status
		for (const jobStatus of Object.values(JobStatusType) as JobStatusType[]) {
			const found = job.attributes.filter((element) => element.status === jobStatus);
			if (found.length === 0) {
				continue;
			}

			list.push({
				title: `Job Status: ${JobStatus.text(jobStatus)}`,
				element: (
					<>
						{found
							// Filter out attributes that should be ignored
							.filter((element) => getJobAttributeIgnores().includes(element.name) === false)
							.map((element) => (
								<Typography key={`job-attribute-${element.id}`} variant={isSmallerDevice ? 'h5' : 'h6'}>
									{getJobAttributeName(element.name)}: <b>{formatJobAttributeValue(element.value)}</b>
								</Typography>
							))}
					</>
				),
			});
		}

		return list;
	}, [job]);

	useComprehensiveTopNav(`Job - ${addPrefix(job.id)}`);

	return (
		<>
			{/* Chat popup component for messaging */}
			<ChatPopup
				isOpen={chatPopupOpen}
				message={message}
				setMessage={setMessage}
				conversation={conversation}
				onMessageSend={handleSend}
				onMessagesRead={handleRead}
				onClose={() => setChatPopupOpen(false)}
			/>
			{/* Main container for the job view */}
			<div className={classes['dispatch-view-container']} ref={containerRef}>
				{/* Job header section with order number, submission time, and actions */}
				<div
					style={{
						padding: isSmallDevice ? '1rem' : '1.5rem 3rem 0.75rem 3rem',
						borderBottom: '1px solid #E5E5E5',
					}}
				>
					{/* Top row with job identification and action buttons */}
					<div
						className={classes['row']}
						style={{
							justifyContent: 'space-between',
						}}
					>
						{/* Left side with order number and submission time */}
						<div className={classes['row']}>
							{/* Order number badge */}
							<div
								className={classes['badge']}
								style={{
									marginTop: '0.5rem',
									marginRight: '1rem',
								}}
							>
								<Typography>
									Order Number:{' '}
									<span
										style={{
											fontWeight: 600,
										}}
									>
										{addPrefix(job.id)}
									</span>
								</Typography>
							</div>
							{/* Job submission timestamp */}
							<div
								className={classes['row']}
								style={{
									marginTop: '0.5rem',
								}}
							>
								<Image src="icClockPrimary" />
								<Typography
									style={{
										marginLeft: '0.5rem',
									}}
								>
									Job submitted at :{' '}
									{adjustTime(settingsState, job.createdAt).format('MM/DD/YYYY hh:mm A')}
								</Typography>
							</div>
						</div>
						{/* Right side with action buttons */}
						<div className={classes['row']}>
							{/* Chat button - only shown if chat is available for current job status */}
							{JobStatus.isChatOpen(job.status) && (
								<button
									className={classes['row']}
									style={{
										marginTop: '0.5rem',
									}}
									onClick={() => setChatPopupOpen(true)}
								>
									<Image
										src="icChat"
										style={{
											marginRight: '0.5rem',
										}}
									/>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Chat</Typography>
									{/* Notification badge showing unread message count */}
									{conversation.messages.filter(
										(message) =>
											message.status !== MessageStatusType.READ && message.userId !== me.id
									).length > 0 && (
										<div className={classes['chat-notifications-number']}>
											<Typography variant={'h6'}>
												{
													conversation.messages.filter(
														(message) =>
															message.status !== MessageStatusType.READ &&
															message.userId !== me.id
													).length
												}
											</Typography>
										</div>
									)}
								</button>
							)}
							{/* Export button (currently disabled) */}
							{/* <button
								className={classes['row']}
								style={{
									marginLeft: '1.5rem',
									marginTop: '0.5rem',
								}}
							>
								<Image src="icExportBlack" />
								<Typography
									variant={isSmallerDevice ? 'h5' : 'h6'}
									style={{
										marginLeft: '0.5rem',
									}}
								>
									Export
								</Typography>
							</button> */}

							{/* Edit button - only shown if user has permission to update job */}
							{canUpdateJob && (
								<button
									className={classes['row']}
									style={{
										marginLeft: '1.5rem',
										marginTop: '0.5rem',
									}}
									onClick={() => push(`/jobs/edit/${job.id}`)}
								>
									<Image src="icEdit" />
									<Typography
										variant={isSmallerDevice ? 'h5' : 'h6'}
										style={{
											marginLeft: '0.5rem',
										}}
									>
										Edit
									</Typography>
								</button>
							)}
						</div>
					</div>

					{/* Job status selector row */}
					<div className={classes['row']} style={{ justifyContent: 'flex-end' }}>
						<div>
							{/* Status dropdown with permission-based editing */}
							<JobStatusSelect
								value={status}
								setValue={setStatus}
								onChangeAction={handleStatusChange}
								isDisabled={!canUpdateJob}
							/>
						</div>
					</div>
					{/* Job details row with key information */}
					<div className={classes['row']}>
						{/* Hospital name section */}
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icThickPlusPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Hospital Name</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.hospital.name}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />

						{/* Date/Time section */}
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icClockPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Date/Time</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.startingAt}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />

						{/* Contact information section */}
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icEmail"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Contact Information</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.contact?.phoneNumber}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '1rem',
											height: '1rem',
										}}
										src="icLocation"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Job Location</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{getLocation(job.hospital.address)}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icThickPlusPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Patient ID</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.patientId ?? '-'}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icThickPlusPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Patient Name</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.patientName ?? '-'}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icThickPlusPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Ordering Physician</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.orderingPhysician ?? '-'}
								<br />
								{physicianPhone(job) ? (
									<a href={`tel:${physicianPhone(job)}`}>{physicianPhone(job)}</a>
								) : (
									'No phone number'
								)}
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icThickPlusPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Room Number</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.roomNumber ?? '-'}
							</Typography>
						</div>
						{job.waitingTime > 0 && (
							<>
								<div className={classes['vertical-separator']} />
								<div
									style={{
										marginTop: isSmallerDevice ? '2rem' : '1rem',
									}}
								>
									<div className={classes['row']}>
										<div className={classes['icon-button']}>
											<Image
												style={{
													width: '0.75rem',
													height: '0.75rem',
												}}
												src="icClockPrimary"
											/>
										</div>
										<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
											Total Waiting Time
										</Typography>
									</div>
									<Typography
										variant={isSmallerDevice ? 'h5' : 'h6'}
										weight="bold"
										style={{
											marginTop: '0.5rem',
										}}
									>
										{toHHMMSS(job.waitingTime)}
									</Typography>
								</div>
							</>
						)}
					</div>
					<div className={classes['row']}>
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icClockPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
									Total Treatment Time (Rounded)
								</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.totalTreatmentTime}m ({job.totalTreatmentTimeRounded}m)
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icClockPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
									Total Waiting Time (Rounded)
								</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.totalWaitingTime}m ({job.totalWaitingTimeRounded}m)
							</Typography>
						</div>
						<div className={classes['vertical-separator']} />
						<div
							style={{
								marginTop: isSmallerDevice ? '2rem' : '1rem',
							}}
						>
							<div className={classes['row']}>
								<div className={classes['icon-button']}>
									<Image
										style={{
											width: '0.75rem',
											height: '0.75rem',
										}}
										src="icClockPrimary"
									/>
								</div>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Total Time (Rounded)</Typography>
							</div>
							<Typography
								variant={isSmallerDevice ? 'h5' : 'h6'}
								weight="bold"
								style={{
									marginTop: '0.5rem',
								}}
							>
								{job.totalTreatmentTime + job.totalWaitingTime}m (
								{job.totalTreatmentTimeRounded + job.totalWaitingTimeRounded}m)
							</Typography>
						</div>
					</div>
				</div>
				{/* Map and status section */}
				<div
					style={{
						padding: isSmallDevice ? '0 1rem 1rem 1rem' : '0rem 3rem 1rem 3rem',
					}}
				>
					<div
						className={classes['row']}
						style={{
							alignItems: 'flex-start',
						}}
					>
						{/* Map view showing hospital location */}
						<div
							className={classes['wrapper']}
							style={{
								width: width <= 931 ? '100%' : '50%',
								minWidth: 300,
								margin: width <= 931 ? '1rem 0 0 0' : '1rem 1rem 0 0',
							}}
						>
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Map: </Typography>
							<MapView
								longitude={job.hospital.address.location[0]}
								latitude={job.hospital.address.location[1]}
								jobId={job.id}
								className={classes['wrapper--map']}
							/>
						</div>

						{/* Status and timeline section */}
						<div
							className={classes['wrapper']}
							style={{
								flex: 1,
								height: '17.5rem',
								marginTop: isSmallerDevice ? '2rem' : '1rem',
								minWidth: 270,
							}}
						>
							{/* Job status with progress indicator */}
							<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Status: </Typography>
							<div className={classes['starts-in-container']}>
								{/* Time until job starts or time since job started */}
								<Typography
									variant={isSmallerDevice ? 'h5' : 'h6'}
									style={{
										alignSelf: 'center',
										textAlign: 'center',
									}}
									color="primary"
								>
									{Timing.to(job.startingAt).includes('ago') ? 'Started ' : 'Starts '}
									<span
										style={{
											fontWeight: 500,
										}}
									>
										{Timing.to(job.startingAt)}
									</span>
								</Typography>

								{/* Progress text and fractional completion */}
								<div
									className={classes['row']}
									style={{ justifyContent: 'space-between', margin: '0.25rem 0' }}
								>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
										{JobStatus.progressText(job.status)}
									</Typography>
									{JobStatus.showProgressFractional(job.status) && (
										<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
											{JobStatus.progressFractional(job.status)}
										</Typography>
									)}
								</div>

								{/* Progress bar visualization */}
								<div className={classes['progress-container']}>
									<div
										className={classes['progress']}
										style={{
											width: `${JobStatus.progressPercentage(job.status)}%`,
										}}
									/>
								</div>
							</div>

							{/* Timeline of job events - only shown if logs exist */}
							{job.logs.length > 0 && (
								<>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Timeline: </Typography>
									<Timeline logs={job.logs} />
								</>
							)}
						</div>
					</div>
				</div>
				{/* Inventory, description, and details section */}
				<div
					style={{
						padding: isSmallDevice ? '1rem' : '1rem 3rem',
					}}
				>
					<div className={classes['row']} style={{ marginTop: '0.5rem' }}>
						{/* Inventory used/assigned section */}
						<div
							style={{
								width: width <= 623 ? '100%' : '33.33%',
								height: '100%',
								minWidth: 300,
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
									Inventory Used/Assigned:{' '}
								</Typography>
								<div
									className={classes['wrapper']}
									style={{
										width: '100%',
										height: '100%',
										marginTop: '0.5rem',
									}}
								>
									{/* Scrollable inventory list */}
									<Scrollbars
										renderThumbVertical={(props) => (
											<div {...props} className={classes['vertical-scroll-thumb']} />
										)}
									>
										{/* Inventory table header */}
										<div
											className={classes['row']}
											style={{
												width: '100%',
												padding: '0rem 1rem 1rem 1rem',
												justifyContent: 'space-between',
											}}
										>
											{/* Name column header */}
											<div className={classes['row']}>
												<Typography
													variant={isSmallerDevice ? 'h5' : 'h6'}
													style={{
														marginRight: '0.5rem',
													}}
												>
													Name
												</Typography>
												<Image src="icDownCaret" />
											</div>
											{/* Available column header */}
											<div className={classes['row']}>
												<Typography
													variant={isSmallerDevice ? 'h5' : 'h6'}
													style={{
														marginRight: '0.5rem',
													}}
												>
													Available
												</Typography>
												<Image src="icDownCaret" />
											</div>
										</div>
										{/* Inventory items list */}
										{job.inventory.map(({ amount, name }) => (
											<div className={classes['inventory-item']} key={ID()}>
												<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>{name}</Typography>
												<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>
													{amount}
												</Typography>
											</div>
										))}
									</Scrollbars>
								</div>
							</div>
						</div>

						{/* Job description section */}
						<div
							style={{
								width: width <= 623 ? '100%' : '33.33%',
								marginTop: width <= 623 ? 32 : 0,
								height: '100%',
								minWidth: 300,
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Description: </Typography>
								<div
									className={classes['wrapper']}
									style={{
										width: '100%',
										height: '100%',
										marginTop: '0.5rem',
									}}
								>
									<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>{job.description}</Typography>
								</div>
							</div>
						</div>

						{/* Job details section with attributes */}
						<div
							style={{
								width: width <= 623 ? '100%' : '33.33%',
								marginTop: width <= 623 ? 32 : 0,
								height: '100%',
								minWidth: 300,
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Details: </Typography>
								<div
									className={classes['wrapper']}
									style={{
										width: '100%',
										height: '100%',
										marginTop: '0.5rem',
										overflowY: 'scroll',
									}}
								>
									{/* Map through attribute details grouped by status */}
									{attributesDetails.map((element, index) => (
										<React.Fragment key={index}>
											<Typography variant="body2" weight="bold">
												{element.title}
											</Typography>
											{element.element}
										</React.Fragment>
									))}
								</div>
							</div>
						</div>
					</div>
					<div className={classes['row']} style={{ marginTop: '1rem' }}>
						<div
							style={{
								width: width <= 623 ? '100%' : '66.66%',
								height: '100%',
								minWidth: 300,
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<div className={classes['jobServices']}>
									<Typography variant="h6" weight="bold">
										Services Provided for Job
									</Typography>
									<div className={classes['jobServices-table']}>
										<div className={classes['jobServices-tableHeader']}>
											<div className={classes['jobServices-tableHeaderItem']}>
												<Typography color="darkgrey" variant="h6">
													Title
												</Typography>
												<Image src="icCaretBlack" />
											</div>
											<div className={classes['jobServices-tableHeaderItem']}>
												<Typography color="darkgrey" variant="h6">
													Description
												</Typography>
												<Image src="icCaretBlack" />
											</div>
										</div>
										<Scrollbars
											renderThumbVertical={(props) => (
												<div {...props} className={classes['vertical-scroll-thumb']} />
											)}
										>
											<div className={classes['jobServices-tableBody']}>
												{job.services.map(({ serviceId, name, description }) => (
													<div
														key={serviceId}
														className={classes['jobServices-tableRow']}
														style={{ margin: '0.5rem 0' }}
													>
														<div>
															<Typography variant="h6">{name}</Typography>
														</div>
														<div>
															<Typography variant="h6">{description}</Typography>
														</div>
													</div>
												))}
											</div>
										</Scrollbars>
									</div>
								</div>
							</div>
						</div>
						<div
							style={{
								width: width <= 623 ? '100%' : '33.33%',
								height: '100%',
								minWidth: 300,
								marginTop: '2rem',
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography
									variant={isSmallerDevice ? 'h5' : 'h6'}
									weight="bold"
									style={{
										marginTop: isSmallerDevice ? '2rem' : '1rem',
									}}
								>
									Custom Cost Amplifiers:{' '}
								</Typography>
								<JobCustomCostList
									items={job.customCosts}
									updateCustomCosts={() => {}}
									defaultCheckedIds={job.customCosts.map(({ customCostId }) => customCostId)}
								/>
							</div>
						</div>
					</div>
					<div className={classes['row']} style={{ margin: '1rem 0' }}>
						<div style={{ width: width <= 623 ? '100%' : '33.33%', minWidth: 300, marginTop: '2rem' }}>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Requirements: </Typography>
								<div
									className={classes['wrapper']}
									style={{
										width: '100%',
										height: '100%',
										marginTop: '0.5rem',
									}}
								>
									<Scrollbars>
										<div className={classes['requirement-tags']}>
											{job.requirements.map(({ id, name }) => (
												<Tag
													key={id}
													initialTitle={name}
													onValueChanged={(value) => console.log(value)}
													editable={false}
												/>
											))}
										</div>
									</Scrollbars>
								</div>
							</div>
						</div>
						<div style={{ width: width <= 623 ? '100%' : '33.33%', minWidth: 300, marginTop: '2rem' }}>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography variant={isSmallerDevice ? 'h5' : 'h6'}>Nurse notes: </Typography>
								<Notes items={job.notes} />
							</div>
						</div>
						<div
							style={{
								width: width <= 623 ? '100%' : '33.33%',
								height: '100%',
								minWidth: 300,
								marginTop: '2rem',
							}}
						>
							<div
								style={{
									width: width <= 623 ? '100%' : '96%',
									height: '15rem',
								}}
							>
								<Typography
									variant={isSmallerDevice ? 'h5' : 'h6'}
									style={{
										marginTop: isSmallerDevice ? '2rem' : '1rem',
									}}
								>
									Downloads:{' '}
								</Typography>
								<div
									style={{
										width: '100%',
										height: '100%',
										marginTop: '0.2rem',
									}}
								>
									<FileList.Downloadable files={job.files ?? []} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default JobScreen;
